/**
 * Helpers for volunteerShifts Firestore docs and UI (admin + public signup).
 */

const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** @param {string} iso YYYY-MM-DD */
export function weekdayNameFromIsoDate(iso) {
  const parts = (iso || '').trim().split('-').map((x) => parseInt(x, 10));
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return '';
  const d = new Date(parts[0], parts[1] - 1, parts[2]);
  if (Number.isNaN(d.getTime())) return '';
  return WEEKDAY_NAMES[d.getDay()] || '';
}

/** @param {string} iso */
export function formatDayTabLabel(iso) {
  const name = weekdayNameFromIsoDate(iso);
  if (!name || !iso) return iso || '—';
  const short = iso.replace(/^\d{4}-/, '');
  return `${name.slice(0, 3)} ${short}`;
}

/** e.g. SAT 3/20 — for volunteer "My shifts" day headings */
export function formatVolunteerCompactDay(iso) {
  const name = weekdayNameFromIsoDate(iso);
  const parts = (iso || '').trim().split('-').map((x) => parseInt(x, 10));
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n)) || !name) return iso || '—';
  const [, m, d] = parts;
  return `${name.slice(0, 3).toUpperCase()} ${m}/${d}`;
}

/** Two-line day tab: WED + 3/17 */
export function formatVolunteerTabParts(iso) {
  const name = weekdayNameFromIsoDate(iso);
  const parts = (iso || '').trim().split('-').map((x) => parseInt(x, 10));
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n)) || !name) {
    return { dayAbbr: '—', dateLine: (iso || '—').toString() };
  }
  const [, m, d] = parts;
  return { dayAbbr: name.slice(0, 3).toUpperCase(), dateLine: `${m}/${d}` };
}

/** Display time ranges with an en dash */
export function formatTimeBlockDisplay(timeBlock) {
  const s = (timeBlock || '').toString().trim();
  if (!s) return '—';
  return s.replace(/\s*-\s*/g, '–');
}

/** Public-facing task blurb: dedicated description, else admin notes. */
export function getShiftPublicDescription(shift) {
  const primary = (shift?.description ?? '').toString().trim();
  if (primary) return primary;
  return (shift?.notes ?? '').toString().trim();
}

/** @param {{ startTime?: string, timeBlock?: string, role?: string }[]} shifts */
export function sortShiftsForDisplay(shifts) {
  return [...(shifts || [])].sort((a, b) => {
    const ta = (a.startTime || '').toString();
    const tb = (b.startTime || '').toString();
    if (ta !== tb) return ta.localeCompare(tb);
    return (a.role || '').localeCompare(b.role || '');
  });
}

/**
 * Parse "HH:mm" or "H:mm" to minutes from midnight; NaN if invalid.
 * @param {string} raw
 */
export function parseHmToMinutes(raw) {
  const s = (raw || '').toString().trim();
  const m = s.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return NaN;
  const h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  if (!Number.isFinite(h) || !Number.isFinite(min) || h > 23 || min > 59) return NaN;
  return h * 60 + min;
}

/**
 * Best-effort interval in minutes from startTime + timeBlock (e.g. "7:00-9:00").
 * @returns {{ start: number, end: number } | null}
 */
export function shiftTimeIntervalMinutes(shift) {
  const start = parseHmToMinutes(shift?.startTime);
  if (Number.isFinite(start)) {
    const block = (shift?.timeBlock || '').toString();
    const parts = block.split(/\s*-\s*/);
    if (parts.length >= 2) {
      const endM = parseHmToMinutes(parts[1].trim());
      if (Number.isFinite(endM) && endM > start) {
        return { start, end: endM };
      }
    }
    const end = start + 120;
    return { start, end };
  }
  const block = (shift?.timeBlock || '').toString();
  const parts = block.split(/\s*-\s*/);
  if (parts.length >= 2) {
    const a = parseHmToMinutes(parts[0].trim());
    const b = parseHmToMinutes(parts[1].trim());
    if (Number.isFinite(a) && Number.isFinite(b) && b > a) {
      return { start: a, end: b };
    }
  }
  return null;
}

/**
 * @param {object[]} myShifts shift objects for one user
 * @returns {Array<{ a: object, b: object }>}
 */
export function findOverlappingShiftPairs(myShifts) {
  const list = myShifts || [];
  const pairs = [];
  for (let i = 0; i < list.length; i++) {
    for (let j = i + 1; j < list.length; j++) {
      const ia = shiftTimeIntervalMinutes(list[i]);
      const ib = shiftTimeIntervalMinutes(list[j]);
      if (!ia || !ib) continue;
      if (ia.start < ib.end && ib.start < ia.end) {
        pairs.push({ a: list[i], b: list[j] });
      }
    }
  }
  return pairs;
}

/** @param {{ date?: string }[]} shifts */
export function distinctSortedDates(shifts) {
  const set = new Set((shifts || []).map((s) => (s.date || '').toString().trim()).filter(Boolean));
  return [...set].sort();
}

/**
 * Unique people (uid + displayName) who appear on any shift assignee list — for admin roster picker.
 * @param {object[]} shifts
 * @returns {{ uid: string, displayName: string }[]}
 */
export function collectVolunteersFromShifts(shifts) {
  const byUid = new Map();
  for (const s of shifts || []) {
    for (const a of Array.isArray(s.assignees) ? s.assignees : []) {
      if (!a || typeof a.uid !== 'string') continue;
      const uid = a.uid.trim();
      if (!uid) continue;
      const name = (a.displayName || '').toString().trim();
      const prev = byUid.get(uid);
      if (!prev) {
        byUid.set(uid, { uid, displayName: name || uid });
      } else if (name && (!prev.displayName || prev.displayName === uid || name.length > prev.displayName.length)) {
        byUid.set(uid, { uid, displayName: name });
      }
    }
  }
  return [...byUid.values()].sort((a, b) =>
    (a.displayName || '').localeCompare(b.displayName || '', undefined, { sensitivity: 'base' }),
  );
}

/**
 * Event-day grid: rows = roles, columns = time blocks.
 * @param {object[]} shiftsForOneDay
 */
export function buildEventDayGrid(shiftsForOneDay) {
  const roles = [];
  const timeBlocks = [];
  const roleSet = new Set();
  const tbSet = new Set();
  for (const s of shiftsForOneDay || []) {
    const r = (s.role || '').toString().trim() || 'Role';
    const tb = (s.timeBlock || '').toString().trim() || 'Time';
    roleSet.add(r);
    tbSet.add(tb);
  }
  roles.push(...roleSet);
  timeBlocks.push(...tbSet);
  roles.sort((a, b) => a.localeCompare(b));
  timeBlocks.sort((a, b) => {
    const sa = (shiftsForOneDay || []).find((s) => (s.timeBlock || '').toString().trim() === a);
    const sb = (shiftsForOneDay || []).find((s) => (s.timeBlock || '').toString().trim() === b);
    const ia = sa ? parseHmToMinutes(sa.startTime) : NaN;
    const ib = sb ? parseHmToMinutes(sb.startTime) : NaN;
    if (Number.isFinite(ia) && Number.isFinite(ib) && ia !== ib) return ia - ib;
    return a.localeCompare(b);
  });

  const cellMap = new Map();
  for (const s of shiftsForOneDay || []) {
    const r = (s.role || '').toString().trim() || 'Role';
    const tb = (s.timeBlock || '').toString().trim() || 'Time';
    const key = `${r}\t${tb}`;
    if (!cellMap.has(key)) cellMap.set(key, []);
    cellMap.get(key).push(s);
  }
  return { roles, timeBlocks, cellMap };
}

const MINUTES_PER_DAY = 24 * 60;
const TIMELINE_PAD_MIN = 20;

/**
 * Same-day interval for admin timeline. Prefer 24h times (e.g. 17:00); if end parses before start,
 * bumps end by 12h up to twice (e.g. 8:00–5:00 as 5pm, 11:30–1:00 as 1pm).
 * @returns {{ start: number, end: number } | null}
 */
export function resolveShiftIntervalMinutesForTimeline(shift) {
  const block = (shift?.timeBlock || '').toString();
  const parts = block.split(/\s*-\s*/);
  let start = parseHmToMinutes(shift?.startTime);
  if (!Number.isFinite(start) && parts[0]) {
    start = parseHmToMinutes(parts[0].trim());
  }
  let end = NaN;
  if (parts.length >= 2) {
    end = parseHmToMinutes(parts[1].trim());
  }
  if (Number.isFinite(start) && Number.isFinite(end)) {
    let e = end;
    let guard = 0;
    while (e <= start && guard < 2) {
      e += 12 * 60;
      guard += 1;
    }
    if (e > start) {
      if (e > MINUTES_PER_DAY) e = MINUTES_PER_DAY;
      if (e > start) return { start, end: e };
    }
  }
  if (Number.isFinite(start)) {
    return { start, end: Math.min(start + 120, MINUTES_PER_DAY) };
  }
  if (parts.length >= 2) {
    const a = parseHmToMinutes(parts[0].trim());
    let b = parseHmToMinutes(parts[1].trim());
    if (Number.isFinite(a) && Number.isFinite(b)) {
      let e = b;
      let guard = 0;
      while (e <= a && guard < 2) {
        e += 12 * 60;
        guard += 1;
      }
      if (e > a) {
        if (e > MINUTES_PER_DAY) e = MINUTES_PER_DAY;
        if (e > a) return { start: a, end: e };
      }
    }
  }
  return null;
}

/** @param {number} m minutes 0..1440 */
export function formatMinutesAsClock(m) {
  const clamped = Math.max(0, Math.min(Math.round(m), MINUTES_PER_DAY));
  const h = Math.floor(clamped / 60);
  const min = clamped % 60;
  return `${h}:${String(min).padStart(2, '0')}`;
}

/** 12-hour clock, e.g. 8:05 AM (minutes 0..1440). */
export function formatMinutesAsClock12h(m) {
  const clamped = Math.max(0, Math.min(Math.round(m), MINUTES_PER_DAY));
  const h24 = Math.floor(clamped / 60);
  const min = clamped % 60;
  const isPm = h24 >= 12;
  const h12 = h24 % 12 || 12;
  const suf = isPm ? 'PM' : 'AM';
  return `${h12}:${String(min).padStart(2, '0')} ${suf}`;
}

/** Shifts longer than this (minutes) use an all-day style label instead of a raw range. */
const VOLUNTEER_LONG_BLOCK_MINUTES = 4 * 60 + 1;

/**
 * Volunteer signup time pill: 12h range for normal blocks; "All day" when span is long
 * (e.g. 8:00–5:00 after timeline parsing); raw timeBlock fallback when unparseable.
 * @returns {{ variant: 'allday' } | { variant: 'range'; text: string } | { variant: 'fallback'; text: string }}
 */
export function getVolunteerTimePillDisplay(shift) {
  const raw = formatTimeBlockDisplay(shift?.timeBlock || '').trim();
  const iv = resolveShiftIntervalMinutesForTimeline(shift);
  if (!iv || iv.end <= iv.start) {
    return { variant: 'fallback', text: raw || '—' };
  }
  const dur = iv.end - iv.start;
  if (dur > VOLUNTEER_LONG_BLOCK_MINUTES) {
    return { variant: 'allday' };
  }
  const text = `${formatMinutesAsClock12h(iv.start)}–${formatMinutesAsClock12h(iv.end)}`;
  return { variant: 'range', text };
}

/**
 * @param {{ shift: object, interval: { start: number, end: number } }[]} items mutates .lane
 * @returns {number} lane count
 */
function assignTimelineLanes(items) {
  const sorted = [...items].sort((x, y) => x.interval.start - y.interval.start);
  const laneEnds = [];
  for (const it of sorted) {
    let placed = false;
    for (let lane = 0; lane < laneEnds.length; lane += 1) {
      if (laneEnds[lane] <= it.interval.start) {
        it.lane = lane;
        laneEnds[lane] = it.interval.end;
        placed = true;
        break;
      }
    }
    if (!placed) {
      it.lane = laneEnds.length;
      laneEnds.push(it.interval.end);
    }
  }
  return Math.max(1, laneEnds.length);
}

/**
 * Admin day view: linear time axis, one row per role, bars positioned by interval; overlapping bars stack in lanes.
 * @param {object[]} shiftsForOneDay
 */
export function buildVolunteerDayTimeline(shiftsForOneDay) {
  const list = shiftsForOneDay || [];
  const placed = [];
  const unplaced = [];
  for (const shift of list) {
    const interval = resolveShiftIntervalMinutesForTimeline(shift);
    if (!interval || interval.end <= interval.start) {
      unplaced.push(shift);
      continue;
    }
    placed.push({ shift, interval });
  }

  let startMin = Number.POSITIVE_INFINITY;
  let endMin = Number.NEGATIVE_INFINITY;
  for (const p of placed) {
    startMin = Math.min(startMin, p.interval.start);
    endMin = Math.max(endMin, p.interval.end);
  }

  if (!placed.length) {
    return {
      window: null,
      roles: [],
      roleRows: [],
      unplaced,
      hourTicks: [],
    };
  }

  startMin = Math.max(0, startMin - TIMELINE_PAD_MIN);
  endMin = Math.min(MINUTES_PER_DAY, endMin + TIMELINE_PAD_MIN);
  if (endMin <= startMin) {
    endMin = Math.min(MINUTES_PER_DAY, startMin + TIMELINE_PAD_MIN * 2);
  }

  const byRole = new Map();
  for (const p of placed) {
    const r = (p.shift.role || '').toString().trim() || 'Role';
    if (!byRole.has(r)) byRole.set(r, []);
    byRole.get(r).push({ shift: p.shift, interval: { ...p.interval } });
  }

  const roleFirstStart = (role) => {
    const arr = byRole.get(role) || [];
    const mins = arr.map((x) => x.interval.start).filter((n) => Number.isFinite(n));
    return mins.length ? Math.min(...mins) : Number.POSITIVE_INFINITY;
  };

  const roles = [...byRole.keys()].sort((a, b) => {
    const da = roleFirstStart(a);
    const db = roleFirstStart(b);
    if (da !== db) return da - db;
    return a.localeCompare(b);
  });

  const roleRows = roles.map((role) => {
    const items = (byRole.get(role) || []).map((x) => ({
      shift: x.shift,
      interval: x.interval,
      lane: 0,
    }));
    const laneCount = assignTimelineLanes(items);
    return { role, items, laneCount };
  });

  const hourTicks = [];
  const tickStart = Math.floor(startMin / 60) * 60;
  const tickEnd = Math.ceil(endMin / 60) * 60;
  for (let t = tickStart; t <= tickEnd; t += 60) {
    hourTicks.push({ minutes: t, label: formatMinutesAsClock(t) });
  }

  return {
    window: { startMin, endMin },
    roles,
    roleRows,
    unplaced,
    hourTicks,
  };
}

function csvEscape(v) {
  const s = v == null ? '' : String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

/**
 * @param {object[]} shifts
 * @param {string} filename
 */
export function downloadVolunteerShiftsCsv(shifts, filename) {
  const rows = [['date', 'day', 'dayType', 'role', 'timeBlock', 'startTime', 'needed', 'claimed', 'assignees', 'notes']];
  const sorted = [...(shifts || [])].sort((a, b) => {
    const da = (a.date || '').localeCompare(b.date || '');
    if (da !== 0) return da;
    return sortShiftsForDisplay([a, b]).indexOf(a) - sortShiftsForDisplay([a, b]).indexOf(b);
  });
  for (const s of sorted) {
    const assignees = Array.isArray(s.assignees) ? s.assignees.map((x) => x.displayName || x.uid).join('; ') : '';
    rows.push([
      s.date,
      s.day,
      s.dayType,
      s.role,
      s.timeBlock,
      s.startTime,
      s.needed,
      Array.isArray(s.assignees) ? s.assignees.length : 0,
      assignees,
      s.notes,
    ]);
  }
  const body = rows.map((r) => r.map(csvEscape).join(',')).join('\r\n');
  const blob = new Blob([body], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'volunteer-shifts.csv';
  a.click();
  URL.revokeObjectURL(url);
}

export function isShiftFull(shift) {
  const n = Number(shift?.needed);
  const count = Array.isArray(shift?.assignees) ? shift.assignees.length : 0;
  return Number.isFinite(n) && n > 0 && count >= n;
}

/** Heuristic: event day + Judge role + empty slot → highlight for admin. */
export function isHighPriorityGap(shift) {
  if (shift?.dayType !== 'event') return false;
  const role = (shift?.role || '').toLowerCase();
  if (!role.includes('judge')) return false;
  return !isShiftFull(shift);
}

/** First "H:mm" or "HH:mm" segment of timeBlock, else 09:00. */
export function deriveStartTimeFromTimeBlock(timeBlock) {
  const first = (timeBlock || '').toString().split(/\s*-\s*/)[0]?.trim() || '';
  const m = first.match(/(\d{1,2}):(\d{2})/);
  if (!m) return '09:00';
  const h = Math.min(23, parseInt(m[1], 10));
  const min = Math.min(59, parseInt(m[2], 10));
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

/** Sort key (minutes from midnight) for grouping shifts by time block / chronology. */
export function minuteSortKeyForShift(shift) {
  const a = parseHmToMinutes(shift?.startTime);
  if (Number.isFinite(a)) return a;
  return parseHmToMinutes(deriveStartTimeFromTimeBlock(shift?.timeBlock)) || 0;
}
